// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { Utils } from '../../utils';
import { HceVcf } from '../../../../../interfaces/hce/hce-annotation';

const SVTYPE_MAP = {
  DEL: 'Deletion',
  INS: 'Insertion',
  DUP: 'Duplication',
  INV: 'Inversion',
  BND: 'Translocation',
  CNV: 'Other',
} as const;

type InfoMap = Record<string, string[]>;

interface VcfIntermediate {
  chrom: string;
  pos: number;
  id: string;
  info: InfoMap;
}

@Injectable({ providedIn: 'root' })
export class HceVcfParserService {

  public parse(text: string): HceVcf[] {
    const records = Utils.tsv.parse(text);
    if (!records) return [];

    return records
      .filter(cols => !cols[0]?.startsWith('#'))
      .map(cols => this.toIntermediate(cols))
      .map(vcf => this.toHceVcf(vcf))
      .filter((v): v is HceVcf => v !== null);
  }

  private toIntermediate(cols: string[]): VcfIntermediate {
    const chrom = (cols[0] ?? '').trim();
    const posStr = (cols[1] ?? '').trim();
    const id = (cols[2] ?? '').trim();
    const infoStr = (cols[7] ?? '').trim();

    return {
      chrom,
      pos: this.parseBpValue(posStr, 0),
      id,
      info: this.parseInfo(infoStr),
    };
  }

  private parseInfo(input: string): InfoMap {
    return input
      .split(';')
      .map(p => p.trim())
      .filter(Boolean)
      .reduce((acc, p) => {
        const eq = p.indexOf('=');
        if (eq === -1) return acc;

        const key = p.slice(0, eq).trim();
        const value = p.slice(eq + 1).trim();
        if (!key || value === undefined) return acc;

        (acc[key] ??= []).push(value);
        return acc;
      }, {} as InfoMap)
  }

  private toHceVcf(vcf: VcfIntermediate): HceVcf | null {
    const rawType = this.getRawType(vcf.info);
    if (rawType.length === 0) return null;

    const type = this.toType(rawType);
    const svlen = this.getSvLen(vcf.info);
    const start = vcf.pos;
    const end = this.resolveEnd(start, vcf.info, svlen);
    const length = this.resolveLength(start, end, svlen);
    const svId = this.generateSvId(vcf.chrom, start, end, rawType, vcf.id);

    return {
      noOfChr: Utils.chromosome.toId(vcf.chrom) ?? 0,
      chromosome: vcf.chrom,
      svId,
      type: type as any,
      start,
      end,
      length,
    }
  }

  // ===== TYPE =====
  private getRawType(info: InfoMap): string[] {
    return info['SVTYPE'] ?? [];
  }

  private toType(types: string[]): string | string[] {
    return types.length === 1
      ? this.normalizeType(types[0])
      : types.map(t => this.normalizeType(t));
  }

  private normalizeType(input: string): string {
    return SVTYPE_MAP[input.toUpperCase() as keyof typeof SVTYPE_MAP] ?? 'Other';
  }

  // ===== SVLEN =====
  private getSvLen(info: InfoMap): number {
    return this.parseBpValue(info['SVLEN']?.[0] ?? null, 0);
  }

  // ===== end =====
  private resolveEnd(
    start: number,
    info: InfoMap,
    svlen: number
  ): number {
    const end = this.parseBpValue(info['END']?.[0] ?? null, -1);
    if (end > 0) {
      return end;
    }
    if (svlen !== 0) {
      return start + Math.abs(svlen);
    }
    return start + 1;
  }

  // ===== length =====
  private resolveLength(
    start: number,
    end: number,
    svlen: number
  ): number {
    if (svlen !== 0) {
      return Math.abs(svlen);
    }
    const len = end - start;
    return len > 0 ? len : 1;
  }

  // ===== svId =====
  private generateSvId(
    chrom: string,
    start: number,
    end: number,
    rawType: string[],
    rawId: string
  ): string {
    if (rawId && rawId !== '.') {
      return rawId;
    }

    const typeStr = rawType
      .map(t => t.toUpperCase())
      .join(',');

    return `${chrom}:${start}-${end}:${typeStr}`;
  }

  // ===== bp =====
  private parseBpValue(input: string | null, fallback: number): number {
    if (input === null || input === undefined || input === '') {
      return fallback;
    }

    const n = Number(input);
    if (!Number.isFinite(n)) {
      return fallback;
    }

    if (String(input).includes('.')) {
      return Utils.number.convNum(input, fallback);
    }

    return Utils.number.toInt(input, fallback);
  }
}
